class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.returnFocusTo = trigger;
        this.dialog.showModal();
      });
    });

    this.querySelectorAll('[data-cart-drawer-close]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.close());
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('close', () => this.returnFocusTo?.focus());
  }

  close() {
    this.dialog?.close();
  }

  open(returnFocusTo) {
    if (!this.dialog) return;
    this.returnFocusTo = returnFocusTo;
    this.dialog.showModal();
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;

    this.querySelectorAll('button[data-quantity-action]').forEach((button) => {
      button.addEventListener('click', () => {
        const current = Number(this.input.value) || 0;
        const step = Number(this.input.step) || 1;
        const minimum = Number(this.input.min) || 0;
        const next = button.dataset.quantityAction === 'increase'
          ? current + step
          : Math.max(minimum, current - step);

        this.input.value = String(next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

class LocalizationSelector extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('select');
    select?.addEventListener('change', () => select.form?.submit());
  }
}

document.addEventListener('change', (event) => {
  const select = event.target.closest?.('[data-variant-select]');
  if (!select) return;

  const selectedOption = select.selectedOptions[0];
  const sectionId = select.dataset.sectionId;
  const price = document.querySelector(`#Price-${CSS.escape(sectionId)}`);
  const currentPrice = price?.querySelector('[data-price-current]');
  const compareAtPrice = price?.querySelector('[data-price-compare]');
  const productForm = select.closest('form');
  const addButton = productForm?.querySelector('[name="add"]');
  const isAvailable = selectedOption.dataset.available === 'true';

  if (currentPrice) currentPrice.textContent = selectedOption.dataset.price;
  if (compareAtPrice) {
    compareAtPrice.textContent = selectedOption.dataset.compareAtPrice;
    compareAtPrice.hidden = !selectedOption.dataset.compareAtPrice;
  }
  price?.classList.toggle('price--on-sale', Boolean(selectedOption.dataset.compareAtPrice));

  if (selectedOption.dataset.sizeValue) {
    document.querySelectorAll('[data-size-variant-media]').forEach((group) => {
      group.hidden = group.dataset.sizeVariantMedia !== selectedOption.dataset.sizeValue;
    });
  }

  if (addButton) {
    addButton.disabled = !isAvailable;
    addButton.textContent = isAvailable
      ? addButton.dataset.addToCartText
      : addButton.dataset.soldOutText;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('variant', selectedOption.value);
  window.history.replaceState({}, '', url);
});

async function refreshCartDrawer(returnFocusTo, root) {
    const [sectionsResponse, cartResponse] = await Promise.all([
      fetch(`${root}?sections=cart-drawer`, { headers: { Accept: 'application/json' } }),
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    ]);

    if (!sectionsResponse.ok || !cartResponse.ok) {
      window.location.assign(`${root}cart`);
      return;
    }

    const sections = await sectionsResponse.json();
    const cart = await cartResponse.json();
    const currentSection = document.getElementById('shopify-section-cart-drawer');

    if (!currentSection || !sections['cart-drawer']) {
      window.location.assign(`${root}cart`);
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const updatedSection = parsedDocument.getElementById('shopify-section-cart-drawer');

    if (!updatedSection) {
      window.location.assign(`${root}cart`);
      return;
    }

    currentSection.replaceWith(updatedSection);

    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      const count = cart.item_count;
      const isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
      const label = isArabic
        ? (count === 0 ? 'السلة، لا توجد منتجات' : count === 1 ? 'السلة، منتج واحد' : count === 2 ? 'السلة، منتجان' : `السلة، ${count} منتجات`)
        : `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
      trigger.setAttribute('aria-label', label);

      const visibleLabel = trigger.querySelector('[aria-hidden="true"]');
      if (visibleLabel) visibleLabel.textContent = String(count);
    });

    document.querySelector('cart-drawer')?.open(returnFocusTo);
}

window.sophiaCartAdd = async (item, returnFocusTo, beforeOpen) => {
  const root = window.Shopify?.routes?.root || '/';
  const addResponse = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] })
  });
  if (!addResponse.ok) throw new Error('Could not add product');
  beforeOpen?.();
  await refreshCartDrawer(returnFocusTo, root);
};

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-product-form]');
  const cartDrawer = document.querySelector('cart-drawer');
  if (!form || !cartDrawer) return;

  event.preventDefault();

  const addButton = form.querySelector('[name="add"]');
  const originalButtonText = addButton?.textContent;
  if (addButton) addButton.disabled = true;

  const root = window.Shopify?.routes?.root || '/';

  try {
    const addResponse = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });

    if (!addResponse.ok) throw new Error('Unable to add this item to the cart.');

    await refreshCartDrawer(addButton, root);
  } catch (error) {
    console.error(error);
    window.location.assign(`${root}cart`);
  } finally {
    if (addButton) {
      addButton.disabled = false;
      addButton.textContent = originalButtonText;
    }
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-quick-add]');
  if (!button || button.disabled) return;

  const root = window.Shopify?.routes?.root || '/';
  const originalText = button.textContent;
  const isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
  button.disabled = true;
  button.textContent = isArabic ? 'جارٍ الإضافة…' : 'Adding…';

  try {
    const productResponse = await fetch(`${root}products/${encodeURIComponent(button.dataset.handle)}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!productResponse.ok) throw new Error('Product unavailable');
    const product = await productResponse.json();
    const variant = product.variants.find((item) => item.available);
    if (!variant) throw new Error('Product sold out');

    await window.sophiaCartAdd({ id: variant.id, quantity: 1 }, button);
  } catch (error) {
    console.error(error);
    button.textContent = isArabic ? 'تعذرت الإضافة — حاول مجددًا' : 'Could not add — try again';
    window.setTimeout(() => { button.textContent = originalText; }, 3500);
  } finally {
    button.disabled = false;
    if (button.textContent === (isArabic ? 'جارٍ الإضافة…' : 'Adding…')) button.textContent = originalText;
  }
});

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

if (!customElements.get('localization-selector')) {
  customElements.define('localization-selector', LocalizationSelector);
}
